import { FC, useEffect, useState } from 'react';
import { callEvent } from '../../../../utils/Amplitude/AmplitudeUtilities';
import { useLocationStore } from '../../../../stores/locationStore';
import { generateSlidesConfig } from '../../../../introductionModalHelper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTrackUserEvent } from '../../../../utils/api/requests/useMutations';
import { AppPlatforms } from '../../../../API';
import { UserEventTypes } from '../../../../interfaces/UserEventInterface';
import SharedModal from '../SharedModal';
import IntroductionModalBody from './IntroductionModalBody';
import IntroductionModalFooter from './IntroductionModalFooter';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import { themeSizes } from '../../../../styles/globalStyleVars';

interface Props {
  onClose: () => void;
  show: boolean;
  setIsOnboarded?: (showOnboarding: boolean) => void;
  isOnboarded?: boolean;
  showWhatsNewFeatureFlag?: boolean;
  featureType?: string;
}

const IntroductionModal: FC<Props> = ({
  onClose,
  show,
  setIsOnboarded,
  isOnboarded = false,
  showWhatsNewFeatureFlag = false,
  featureType,
}) => {
  const { activeLocation } = useLocationStore();
  const breakpoints = useBreakpoints();
  const isLgAndUp = useMediaQuery(breakpoints.up('lg'));
  const [slideIndex, setSlideIndex] = useState<number>(0);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(false);
  const { ...featureFlags } = useFlags();
  const activeFlags = Object.keys(featureFlags).filter(
    key => featureFlags[key] === true
  );

  const { mutate: markUserOnboarded } = useTrackUserEvent(
    UserEventTypes.COMPLETE_ONBOARDING,
    { platforms: [AppPlatforms.WEB] }
  );

  const { mutate: markWhatsNewSeen } = useTrackUserEvent(
    UserEventTypes.VIEW_WHATS_NEW,
    { platforms: [AppPlatforms.WEB] }
  );

  const modalType = (function () {
    if (featureType) {
      return {
        event: 'learn more about feature',
        type: `${featureType.toLocaleLowerCase()}_feature`,
      };
    }
    if (showWhatsNewFeatureFlag) {
      return {
        event: 'whats new',
        type: 'whats_new',
      };
    }
    return {
      event: 'onboarding',
      type: 'onboarding',
    };
  })();

  const introductionSlides = generateSlidesConfig(modalType.type, activeFlags);
  const INTRO_MODAL_MOBILE_BODY_MAX_HEIGHT = `calc(90vh - (200px + ${themeSizes.xxl}))`;

  const introBodyStyle: React.CSSProperties = {
    height: isLgAndUp ? undefined : INTRO_MODAL_MOBILE_BODY_MAX_HEIGHT,
    maxHeight: isLgAndUp ? undefined : INTRO_MODAL_MOBILE_BODY_MAX_HEIGHT,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  //marks flag completed after the last slide is viewed
  useEffect(() => {
    if (slideIndex + 1 !== introductionSlides.length) return;
    setOnboardingComplete(true);
  }, [slideIndex]);

  const setUserOnboarded = () => {
    //run onboarding status mutation if user hasn't viewed the onboarding before and it's not the what's new feature modal
    if (!isOnboarded && !showWhatsNewFeatureFlag) {
      markUserOnboarded();
      setIsOnboarded && setIsOnboarded(true);
    }
    // mark whats new as seen when the modal is closed
    if (showWhatsNewFeatureFlag) {
      markWhatsNewSeen();
    }
  };

  const closeModal = () => {
    onClose();
    setSlideIndex(0);
    setUserOnboarded();

    callEvent(
      onboardingComplete
        ? `${modalType.event} complete`
        : `${modalType.event} cancelled`,
      {
        locationNum: activeLocation,
        path: window.location.href,
        slidesViewed: `${slideIndex + 1} out of ${introductionSlides.length}`,
        featureType: featureType,
      }
    );
  };

  return (
    <SharedModal
      bodyStyle={introBodyStyle}
      contentStyle={{ paddingInline: isLgAndUp ? '64px' : themeSizes.md }}
      dataTestId='introduction-modal'
      onClose={closeModal}
      show={show}
      size='xl'
      text={{
        body: (
          <IntroductionModalBody
            isLgAndUp={isLgAndUp}
            setSlideIndex={setSlideIndex}
            slideIndex={slideIndex}
            introductionSlides={introductionSlides}
            activeFlags={activeFlags}
            onClose={closeModal}
          />
        ),
        footer: featureType ? (
          <IntroductionModalFooter
            onClose={closeModal}
            isLgAndUp={isLgAndUp}
            activeLocation={activeLocation}
            kbArticle={introductionSlides[slideIndex]?.kbArticle}
            setUserOnboarded={setUserOnboarded}
            showWhatsNewFeatureFlag={showWhatsNewFeatureFlag}
            featureType={featureType}
          />
        ) : undefined,
        header: isLgAndUp
          ? introductionSlides[slideIndex]?.header
          : introductionSlides[slideIndex]?.headerMobile,
      }}
    />
  );
};

export default IntroductionModal;
